import React, { FormEvent, useEffect, useState, useRef } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { getAbsoluteUrl } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/hooks/getAbsolutePath.hook";
import { GET_CMS_SITE_ORIGIN } from "@danishagro/shared/src/helpers/urlRoot.helper";
import { pushToDataLayer } from "@danishagro/shared/src/gtm/useTagManger";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import { Receipt } from "../Receipt/Receipt.component";
import { DynamicField } from "./components/DynamicField.component";
import { Condition, Field, FormProps } from "./Form.interface";
import { useValidation } from "./hooks/useValidation.hook";
import { useFileUpload } from "./hooks/useFileUpload.hook";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./Form.module.scss";

export const Form = (props: FormProps) => {
    const { errorMessages, validate, addErrors, getError, validateField } = useValidation(
        props.formData
    );
    const { checkForFileFields, uploadFile } = useFileUpload();
    const [showValidationSummary, setValidationSummary] = useState(false);
    const [showReceiptModule, setShowReceiptModule] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [validationErrorTitle, setValidationErrorTitle] = useState("");

    const [viewState, setViewState] = useState("INIT");

    const { scrollTo } = useScreen();

    const [submitted, setSubmitted] = useState(false);

    const [formState, setFormState] = useState<Record<string, string | string[]>>({});

    const handleInputChange = (alias: string, value: string | string[]) => {
        setFormState((prev) => ({ ...prev, [alias]: value }));
        validateField(alias, value);
    };

    function getFieldAliasById(id: string): string | undefined {
        for (const fieldset of props.formData.allFieldSets) {
            for (const container of fieldset.containers) {
                for (const field of container.fields) {
                    if (field.id === id) return field.alias;
                }
            }
        }
        return undefined;
    }

    // Helper to get the field by id
    function getFieldById(id: string): Field | undefined {
        for (const fieldset of props.formData.allFieldSets) {
            for (const container of fieldset.containers) {
                for (const field of container.fields) {
                    if (field.id === id) return field;
                }
            }
        }
        return undefined;
    }

    const evaluateCondition = (
        condition: Condition | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state: Record<string, any>
    ): boolean => {
        if (!condition || !condition.enabled || !condition.rules.length) return true;

        return condition.rules.every((rule) => {
            // Convert rule.field (id) to alias
            const alias = getFieldAliasById(rule.field);
            if (!alias) return false; // If not found, fail the condition

            const fieldValue = state[alias];
            switch (rule.operator) {
                case 0: // equality
                    return fieldValue === rule.value;
                // extend with other operators if necessary
                default:
                    return false;
            }
        });
    };

    // Helper function to determine if a field is a checkbox
    function isCheckboxField(key: string): boolean {
        // Implement logic to determine if a key belongs to a checkbox field
        // For example, based on the fieldTypeId or structure of your form fields
        return props.formData.allFieldSets.some((fieldset) =>
            fieldset.containers.some((container) =>
                container.fields.some(
                    (field) =>
                        field.fieldTypeId === "fab43f20-a6bf-11de-a28f-9b5755d89593" &&
                        field.alias === key
                )
            )
        );
    }

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();

        try {
            const form = new FormData(event.target as HTMLFormElement);

            const body = {} as FormProps;
            const datePattern = /^\d{2}\.\d{2}\.\d{4}$/;
            const checkboxValues: Record<string, string[]> = {};

            // Build request body
            form.forEach((value, key) => {
                // Date picker (dd.mm.yyyy -> yyyy/mm/dd)
                if (typeof value === "string" && datePattern.test(value)) {
                    const [dd, mm, yyyy] = value.split(".");
                    body[key] = `${yyyy}/${mm}/${dd}`;
                }
                // File picker
                else if (value instanceof File) {
                    body[key] = value && value.name !== "" ? value : "";
                }
                // Checkbox (aggregate into arrays)
                else if (isCheckboxField(key)) {
                    if (!checkboxValues[key]) checkboxValues[key] = [];
                    checkboxValues[key].push(String(value));
                }
                // Everything else
                else {
                    body[key] = value as string;
                }
            });

            // Merge checkbox arrays
            for (const key in checkboxValues) {
                body[key] = checkboxValues[key];
            }

            // 1) Validate
            const [isValid] = validate(body);
            if (!isValid) return;

            setViewState("SUBMITTING");

            // 2) Upload files if present
            const { hasFile, fileFields } = checkForFileFields(body);
            if (hasFile) {
                try {
                    await Promise.all(
                        fileFields.map(async (field) => {
                            const [didUpload, fileUrl] = await uploadFile(body?.[field]);
                            if (didUpload && field) {
                                body[field] = fileUrl;
                            } else {
                                console.error("File upload failed for field:", field);
                                throw new Error("FILE_UPLOAD_FAILED");
                            }
                        })
                    );
                } catch (e) {
                    console.error("One or more file uploads failed:", e);
                    alert("Something went wrong while uploading files. Please try again.");
                    return; // stop submit if upload fails
                }
            }

            // 3) Submit form (with timeout + catch)
            const url = `${GET_CMS_SITE_ORIGIN(
                "ABSOLUTE"
            )}/submitform?url=/umbraco/forms/api/v1/entries/${props.formData.id}`;

            const controller = new AbortController();
            const timeoutMs = 15000;
            const timeoutId = window.setTimeout(() => controller.abort("timeout"), timeoutMs);

            let response: Response;

            try {
                response = await fetch(getAbsoluteUrl(url, "SPA"), {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Api-Key": "88291c19-c109-p!c0-4227-97d2-3e95b7084b92",
                    },
                    body: JSON.stringify({ values: body }),
                    signal: controller.signal,
                });
            } catch (err) {
                // Network error, DNS resolution, CORS, or aborted (timeout)
                console.error("Network/Fetch error during form submit:", err);
                const isAbort =
                    (err as DOMException)?.name === "AbortError" ||
                    (typeof err === "string" && err === "timeout");
                alert(
                    isAbort
                        ? "The connection took too long. Please try again in a moment."
                        : "Something went wrong while submitting the form. Please try again."
                );
                return;
            } finally {
                clearTimeout(timeoutId);
            }

            if (response.ok) {
                const data = await response.json();
                if (data?.statusCode === 422) {
                    // backend-validations in payload, not HTTP status
                    console.error("Server returned validation error payload:", data);
                    setValidationErrorTitle(data?.title ?? "Something went wrong");
                    if (Array.isArray(data?.errors)) addErrors(data.errors);
                } else {
                    setSubmitted(true);
                    pushToDataLayer({
                        event: "contact_form",
                    });
                    setShowReceiptModule(true);
                }
            } else if (response.status === 422) {
                // HTTP 422 with error payload
                const data = await response.json().catch(() => null);
                setValidationErrorTitle(data?.title ?? "Please fill in the marked fields.");
                if (Array.isArray(data?.errors)) addErrors(data.errors);
            } else {
                console.error("Unexpected HTTP status:", response.status);
                alert("Something went wrong. Please try again later.");
            }
        } catch (err) {
            // Any unexpected runtime error in our logic
            console.error("Unexpected error in onSubmit:", err);
            alert("Something went wrong. Please try again.");
        } finally {
            setViewState("INIT");
            setValidationSummary(true);
        }
    };

    useEffect(() => {
        if (submitted) {
            scrollTo(containerRef.current.offsetParent);
            setShowReceiptModule(true);

            const timer = setTimeout(() => {
                setSubmitted(false);
                setShowReceiptModule(false);
            }, 15000);

            return () => clearTimeout(timer);
        }
    }, [submitted, scrollTo]);

    return (
        <div className={S.form} ref={containerRef}>
            {/** Form Header */}
            {!showReceiptModule && (props.formTagline || props.formHeadline) ? (
                <div className={S.textWrapper}>
                    {/** Form Tagline */}
                    {props.formTagline ? <span>{props.formTagline}</span> : null}

                    {/** Form Headline */}
                    {props.formHeadline ? (
                        <DA_Title h3 tag={DA_TitleTag.H3} noMargin fullWidth>
                            {props.formHeadline}
                        </DA_Title>
                    ) : null}
                </div>
            ) : null}

            {/** Form */}

            {!showReceiptModule ? (
                <form onSubmit={onSubmit} noValidate>
                    <fieldset className={S.fieldset} disabled={viewState === "SUBMITTING"}>
                        {/** Validation summary */}
                        {showValidationSummary && (
                            <>
                                {props.formData.showValidationSummary === true ? (
                                    <ul className={S.summaryList}>
                                        {errorMessages && (
                                            <li className={S.summaryItem}>
                                                <h4>{validationErrorTitle}</h4>
                                            </li>
                                        )}

                                        {errorMessages?.map((error, key) => {
                                            return (
                                                <li key={key} className={S.summaryItem}>
                                                    {error.alias}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null}
                            </>
                        )}

                        {/** Groups */}

                        {props.formData.allFieldSets.map((fieldset) =>
                            fieldset.containers.map((container, idx) => (
                                <div
                                    key={idx}
                                    className={cn(S.container, S[`size_${container.width}`])}
                                >
                                    {container.fields.map((field) => {
                                        let shouldRender = true;

                                        // Only evaluate condition if the dependency is a dropdown
                                        if (
                                            field?.condition?.enabled &&
                                            field?.condition?.rules?.length
                                        ) {
                                            // Assume only one rule for simplicity, otherwise loop
                                            const rule = field?.condition?.rules[0];
                                            const depField = getFieldById(rule?.field);

                                            // Only apply condition if the referenced field is a select
                                            if (
                                                depField &&
                                                depField?.fieldTypeId ===
                                                    "0dd29d42-a6a5-11de-a2f2-222256d89593"
                                            ) {
                                                shouldRender = evaluateCondition(
                                                    field?.condition,
                                                    formState
                                                );
                                            }
                                        }

                                        return shouldRender ? (
                                            <DynamicField
                                                key={field.alias}
                                                field={field}
                                                formData={props.formData}
                                                getError={getError}
                                                onInputChange={handleInputChange}
                                                // formState={formState}
                                            />
                                        ) : null;
                                    })}
                                </div>
                            ))
                        )}

                        {/** Submit */}
                        <div>
                            {viewState === "SUBMITTING" && <DA_Spinner className={S.spinner} />}

                            <DA_Button
                                title={props.formData.submitLabel}
                                submit
                                disabled={viewState === "SUBMITTING"}
                            />
                        </div>
                    </fieldset>
                </form>
            ) : null}

            {/** Receipt */}
            {showReceiptModule ? <Receipt {...props.receipt} /> : null}
        </div>
    );
};

export default Form;
